/* Flamingo Royale Art | Vier-Motive-Abstimmung
 * Anonyme Vorzugsstimme per Klick. Gleichmäßig verteilte Motive / Stimmen
 * werden ausschließlich über die separat installierte Edge Function gespeichert.
 */
(() => {
  'use strict';

  const root = document.getElementById('fr-art-poll-root');
  if (!root) return;

  const params = new URLSearchParams(window.location.search);
  const previewMode = params.get('kunstwahl_vorschau') === '1';
  const enabled = root.dataset.enabled === 'true';
  const adsOnly = root.dataset.onlyAds === 'true';
  const requireConsent = root.dataset.consent !== 'false';
  const campaign = root.dataset.campaign || 'flamingo_herbst_2026';
  const permittedPage = ['index', 'product', 'collection', 'page', 'search'].includes(root.dataset.pageType);
  const delayMs = Math.max(3000, Math.min(30000, Number(root.dataset.delay || 8) * 1000));

  if (!previewMode && (!enabled || !permittedPage || (adsOnly && params.get('kunstwahl') !== '1'))) return;
  if (!/^[a-z0-9_-]{1,48}$/.test(campaign)) return;
  if (window.Shopify && window.Shopify.designMode && !previewMode) return;

  const endpoint = (root.dataset.endpoint || '').trim();
  if (!previewMode) {
    try {
      const parsed = new URL(endpoint);
      if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.search || parsed.hash) return;
    } catch (_) { return; }
  }

  const dialog = root.querySelector('[data-art-poll-dialog]');
  const optionsBox = root.querySelector('[data-art-poll-options]');
  const noneButton = root.querySelector('[data-art-poll-none]');
  const closeButton = root.querySelector('[data-art-poll-close]');
  const feedback = root.querySelector('[data-art-poll-feedback]');
  const previewBanner = root.querySelector('[data-art-poll-preview]');
  if (!dialog || typeof dialog.showModal !== 'function' || !optionsBox || !noneButton || !closeButton) return;

  const products = new Map();
  let catalogPromise = null;

  // Section Rendering läuft auf der gewählten Kollektions-URL. Die dortige
  // paginate-Anweisung bleibt vollständig von der Shopgalerie getrennt.
  async function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const collectionUrl = new URL(root.dataset.collectionUrl || '', window.location.origin);
      if (collectionUrl.origin !== window.location.origin || !collectionUrl.pathname.includes('/collections/')) {
        throw new Error('Invalid Shopify collection URL');
      }
      collectionUrl.search = '';
      collectionUrl.searchParams.set('section_id', 'art-poll-catalog');
      if (params.has('preview_theme_id')) {
        collectionUrl.searchParams.set('preview_theme_id', params.get('preview_theme_id'));
      }
      const response = await fetch(collectionUrl.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Shopify catalog could not be loaded');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-fr-art-poll-catalog] [data-id]').forEach((item) => {
        const id = item.dataset.id;
        if (/^\d{6,20}$/.test(id || '') && item.dataset.image && !products.has(id)) {
          products.set(id, { id, title: item.dataset.title || 'Kunstmotiv', image: item.dataset.image });
        }
      });
      if (products.size < 4) throw new Error('Fewer than four products with images in poll collection');
    })();
    return catalogPromise;
  }

  const seenKey = `fr-art-poll:seen:${campaign}`;
  const alreadyParticipated = () => {
    try { return window.localStorage.getItem(seenKey) === '1'; } catch (_) { return false; }
  };
  const rememberParticipation = () => {
    if (previewMode) return;
    try { window.localStorage.setItem(seenKey, '1'); } catch (_) { /* storage can be blocked */ }
  };

  let alreadyScheduled = false;
  let opened = false;
  let answered = false;
  let pending = false;
  let sessionId = null;
  let shownIds = [];

  async function request(payload, keepalive = false) {
    const ctrl = new AbortController();
    const timeout = window.setTimeout(() => ctrl.abort(), 9000);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        keepalive,
        signal: ctrl.signal,
        body: JSON.stringify({ ...payload, campaign })
      });
      if (!response.ok) throw new Error(`Poll API ${response.status}`);
      const data = await response.json();
      if (!data || data.ok !== true) throw new Error('Poll API: missing confirmation');
      return data;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function randomFour() {
    const ids = Array.from(products.keys());
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids.slice(0, 4);
  }

  function renderOptions(ids) {
    const fragment = document.createDocumentFragment();
    ids.forEach((id, index) => {
      const product = products.get(id);
      if (!product) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fr-art-poll__option';
      button.setAttribute('aria-label', `Motiv ${index + 1}: ${product.title} wählen`);
      button.dataset.choice = id;

      const imgWrap = document.createElement('span');
      imgWrap.className = 'fr-art-poll__img-wrap';
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'eager';
      image.decoding = 'async';
      imgWrap.appendChild(image);
      const label = document.createElement('span');
      label.className = 'fr-art-poll__number';
      label.textContent = `Motiv ${index + 1}`;
      button.append(imgWrap, label);
      button.addEventListener('click', () => castVote(id));
      fragment.appendChild(button);
    });
    optionsBox.replaceChildren(fragment);
  }

  function setBusy(busy) {
    pending = busy;
    optionsBox.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
    noneButton.disabled = busy;
  }

  function message(text, error = false) {
    feedback.textContent = text;
    feedback.hidden = false;
    feedback.classList.toggle('fr-art-poll__feedback--error', error);
  }

  async function castVote(choice) {
    if (pending || answered || !opened || (choice !== 'none' && !shownIds.includes(choice))) return;
    feedback.hidden = true;
    setBusy(true);
    if (previewMode) {
      answered = true;
      message('Vorschau: Deine Auswahl wurde nicht gespeichert.');
      window.setTimeout(() => dialog.close(), 1400);
      return;
    }
    try {
      await request({ action: 'vote', session_id: sessionId, choice });
      answered = true;
      rememberParticipation();
      message('Vielen Dank! Deine Stimme wurde gezählt.');
      window.setTimeout(() => { if (dialog.open) dialog.close(); }, 1550);
    } catch (_) {
      message('Deine Stimme konnte nicht gespeichert werden. Bitte versuche es erneut.', true);
      setBusy(false);
    }
  }

  function dismiss() {
    if (!opened || !dialog.open || pending) return;
    if (!answered && !previewMode) {
      rememberParticipation();
      if (sessionId) {
        // Das Protokollieren des Schließens ist optional. Es blockiert die UI nicht.
        request({ action: 'dismiss', session_id: sessionId }, true).catch(() => {});
      }
    }
    dialog.close();
  }

  closeButton.addEventListener('click', dismiss);
  noneButton.addEventListener('click', () => castVote('none'));
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    dismiss();
  });
  dialog.addEventListener('click', (event) => {
    // Nur ein echter Klick auf den abgedunkelten Bereich soll schließen.
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismiss();
  });

  async function showPoll() {
    if (opened || (!previewMode && alreadyParticipated()) || document.visibilityState === 'hidden') return;
    try { await loadCatalog(); } catch (_) { return; }
    let ids;
    if (previewMode) {
      ids = randomFour();
    } else {
      try {
        const result = await request({ action: 'start', candidates: Array.from(products.values()).map(({ id, title }) => ({ id, title: title.slice(0, 120) })) });
        if (!Array.isArray(result.options) || result.options.length !== 4 ||
            new Set(result.options).size !== 4 || result.options.some((id) => !products.has(String(id))) ||
            !/^[a-f0-9-]{36}$/i.test(result.session_id || '')) return;
        ids = result.options.map(String);
        sessionId = result.session_id;
      } catch (_) {
        // Ohne Datenbankverbindung niemals eine scheinbar gültige Abstimmung zeigen.
        return;
      }
    }
    shownIds = ids;
    renderOptions(ids);
    if (optionsBox.children.length !== 4) return;
    feedback.hidden = true;
    previewBanner.hidden = !previewMode;
    root.hidden = false;
    try {
      dialog.showModal();
      opened = true;
      closeButton.focus({ preventScroll: true });
    } catch (_) { root.hidden = true; }
  }

  function startAfterDelay() {
    if (alreadyScheduled || (!previewMode && alreadyParticipated())) return;
    alreadyScheduled = true;
    window.setTimeout(() => {
      if (document.visibilityState === 'hidden') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') showPoll();
        }, { once: true });
      } else showPoll();
    }, previewMode ? 250 : delayMs);
  }

  if (previewMode) { startAfterDelay(); return; }
  // Read localStorage only after the consent gate has approved analytics.
  if (!requireConsent) { startAfterDelay(); return; }

  const permittedByShopify = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
    catch (_) { return false; }
  };
  const checkPermission = () => { if (permittedByShopify()) startAfterDelay(); };
  document.addEventListener('visitorConsentCollected', checkPermission);

  if (window.Shopify?.customerPrivacy) {
    checkPermission();
  } else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) checkPermission();
    });
  }
})();
